import { Card } from 'components/Card';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL_BACK } from 'store/actions';
import { doSetupEngageUpdateGeneral } from 'store/remote/actions';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { cloneDeep, isEqual } from 'lodash';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { CardRow } from 'components/CardRow';
import { smartText } from 'lib/str';
import { ICONS } from 'components/Icon';
import { TextField } from 'components/Field/TextField';

export const EngageSettingsReportIssueModal: React.FC<
  ModalScreenProps<ModalScreens.SettingsEngageReportIssue>
> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const forceUpdate = useForceUpdate();
  const confirmUnsaved = useConfirmUnsaved();

  const { reportIssue } = route.params;

  const dataRef = React.useRef(cloneDeep(reportIssue));
  const isDirty = React.useCallback(
    () => !isEqual(reportIssue, dataRef.current),
    [reportIssue],
  );
  const isDirtyRef = React.useRef<boolean>(false);
  isDirtyRef.current = isDirty();

  const handleSave = React.useCallback(() => {
    if (isDirty()) {
      dispatch(doSetupEngageUpdateGeneral({ data: dataRef.current }));
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [dispatch, isDirty]);

  const handleClose = React.useCallback(() => {
    if (isDirty()) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [confirmUnsaved, dispatch, handleSave, isDirty]);

  const handleChangeValue = <K extends keyof typeof dataRef.current>(
    key: K,
    value: (typeof dataRef.current)[K],
    doForceUpdate: boolean = false,
  ) => {
    dataRef.current[key] = value;
    if (doForceUpdate || isDirtyRef.current !== isDirty()) {
      forceUpdate();
    }
  };

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirty() });
  }, [navigation, isDirty]);
  useEsc(handleClose);

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading="Report an Issue"
          cancelButtonText="Close"
          onPressCancelButton={handleClose}
          onPressSaveButton={handleSave}
        />
      }
    >
      <Card
        explanation={smartText(
          'You must have a description, website, email, OR phone for "Report an Issue" to appear',
        )}
      >
        <CardRow
          label={smartText('Enable "Report an Issue"')}
          checkedValue={dataRef.current.enableReportIssue}
          onPress={() =>
            handleChangeValue(
              'enableReportIssue',
              !dataRef.current.enableReportIssue,
              true,
            )
          }
        />
      </Card>

      <Card marginBottomQuarter>
        <TextField
          placeholder="Title (Default: Report an Issue)"
          onChange={(value) => handleChangeValue('reportIssueTitle', value)}
          value={dataRef.current.reportIssueTitle}
          onEsc={handleClose}
          autoFocusWeb
          autoFocusDelay
          maxLength={240}
        />
      </Card>

      <Card>
        <TextAreaField
          placeholder="Description"
          onChange={(value) =>
            handleChangeValue('reportIssueDescription', value)
          }
          value={dataRef.current.reportIssueDescription}
          onEsc={handleClose}
          maxLength={6000}
        />
      </Card>

      <Card>
        <TextField
          iconLeft={ICONS.GlobeSolid}
          placeholder="Website"
          onChange={(value) => handleChangeValue('reportIssueWebsite', value)}
          value={dataRef.current.reportIssueWebsite}
          onEsc={handleClose}
          autoComplete="url"
          autoCorrect={false}
          autoCapitalize="none"
          maxLength={500}
        />
        <TextField
          iconLeft={ICONS.CircleEnvelopeSolid}
          placeholder="Email Address"
          onChange={(value) => handleChangeValue('reportIssueEmail', value)}
          value={dataRef.current.reportIssueEmail}
          onEsc={handleClose}
          autoComplete="email"
          autoCorrect={false}
          autoCapitalize="none"
          maxLength={240}
        />
        <TextField
          iconLeft={ICONS.CirclePhoneSolid}
          placeholder="Phone Number"
          onChange={(value) => handleChangeValue('reportIssuePhone', value)}
          value={dataRef.current.reportIssuePhone}
          onEsc={handleClose}
          autoComplete="phone"
          autoCorrect={false}
          autoCapitalize="none"
          maxLength={240}
        />
      </Card>
    </PageScrollView>
  );
};
